/**
 * Pure conversion logic for the wait-duration editor ({@link WaitDurationInput}). A `wait` step ALWAYS
 * stores its length as canonical DAYS (`waitDays` — what the engine waits); `weeks` is a display unit
 * (`waitDays = value × 7`). These helpers own the days↔weeks math so it can be unit-tested away from React.
 *
 * Note the round-trip is intentionally lossy: switching units re-expresses the value in whole units of the
 * new unit (weeks round to the nearest whole week), so e.g. 10 days → weeks shows "1 week" and commits 7
 * canonical days. The server anchors a weeks wait to the coming Monday regardless of the exact day count.
 */
export type WaitUnit = 'days' | 'weeks';

export const MAX_DAYS = 365;
export const MAX_WEEKS = 52;

/** Canonical wait length (always in DAYS) for a value entered in `unit`. */
export function toDays(value: number, unit: WaitUnit): number {
  return unit === 'weeks' ? value * 7 : value;
}

/** The value to show in `unit` for a canonical wait length in days (weeks round to the nearest whole week, min 1). */
export function fromDays(days: number, unit: WaitUnit): number {
  return unit === 'weeks' ? Math.max(1, Math.round(days / 7)) : days;
}

/** Clamp an entered value into the legal range for its unit (1–365 days / 1–52 weeks). */
export function clampWaitValue(n: number, unit: WaitUnit): number {
  return Math.max(1, Math.min(unit === 'weeks' ? MAX_WEEKS : MAX_DAYS, n));
}

/**
 * Resolve a raw input string on blur/Enter: parse → round → clamp (or fall back to the current canonical
 * `fallbackDays` when the field is non-numeric). Returns both the value to redisplay and the canonical days
 * to persist.
 */
export function commitWaitValue(
  raw: string,
  unit: WaitUnit,
  fallbackDays: number,
): { value: number; days: number } {
  const parsed = Math.round(Number(raw));
  const value = Number.isFinite(parsed) ? clampWaitValue(parsed, unit) : fromDays(fallbackDays, unit);
  return { value, days: toDays(value, unit) };
}

/**
 * Re-express the current value when the unit toggle flips (days↔weeks): clamp it in the OLD unit, convert to
 * canonical days, then round into whole units of the NEW unit. Returns the value to redisplay and the
 * canonical days to persist. Non-numeric input falls back to the current canonical `fallbackDays`.
 */
export function reexpressWaitForUnit(
  raw: string,
  fromUnit: WaitUnit,
  toUnit: WaitUnit,
  fallbackDays: number,
): { value: number; days: number } {
  const parsed = Math.round(Number(raw));
  const curDays = Number.isFinite(parsed) ? toDays(clampWaitValue(parsed, fromUnit), fromUnit) : fallbackDays;
  const value = fromDays(curDays, toUnit);
  return { value, days: toDays(value, toUnit) };
}
